
import React from "react";
import { Edit, Trash2, ToggleLeft, ToggleRight, Link } from "lucide-react";
import { Account } from "@/types/strategy-types";
import { Button } from "@/components/ui/button";
import FollowLinksUploadDialog from "./FollowLinksUploadDialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

interface AccountActionsProps {
  account: Account;
  onEdit: (account: Account) => void;
  onDelete: (account: Account) => void;
  onToggleActive: (id: string, active: boolean) => Promise<void>;
  userRole?: string | null;
}

const AccountActions = ({
  account,
  onEdit,
  onDelete,
  onToggleActive,
  userRole
}: AccountActionsProps) => {
  const [isFollowLinksDialogOpen, setIsFollowLinksDialogOpen] = React.useState(false);

  const handleToggleActive = async () => {
    await onToggleActive(account.id, !account.active);
  };
  
  const handleEdit = () => {
    onEdit(account);
  };
  
  const handleDelete = () => {
    onDelete(account);
  };
  
  const handleUploadLinks = () => {
    setIsFollowLinksDialogOpen(true);
  };

  const isAdmin = userRole === 'admin';
  const isStrategyProvider = userRole === 'strategy_provider';
  const canEdit = isAdmin || isStrategyProvider;
  
  return (
    <>
      <div className="flex items-center justify-end space-x-1">
        <TooltipProvider>
          {canEdit && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="icon" onClick={handleEdit} className="h-8 w-8">
                  <Edit className="h-4 w-4" />
                  <span className="sr-only">Edit</span>
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Edit account</p>
              </TooltipContent>
            </Tooltip>
          )}
          
          {canEdit && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="icon" onClick={handleToggleActive} className="h-8 w-8">
                  {account.active ? (
                    <>
                      <ToggleLeft className="h-4 w-4" />
                      <span className="sr-only">Deactivate</span>
                    </>
                  ) : (
                    <>
                      <ToggleRight className="h-4 w-4" />
                      <span className="sr-only">Activate</span>
                    </>
                  )}
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>{account.active ? 'Deactivate' : 'Activate'} account</p>
              </TooltipContent>
            </Tooltip>
          )}

          {canEdit && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="icon" onClick={handleUploadLinks} className="h-8 w-8">
                  <Link className="h-4 w-4" />
                  <span className="sr-only">Upload Follow Links</span>
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Upload follow links</p>
              </TooltipContent>
            </Tooltip>
          )}
          
          {isAdmin && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="icon" onClick={handleDelete} className="h-8 w-8 text-red-500 hover:text-red-600 hover:bg-red-50">
                  <Trash2 className="h-4 w-4" />
                  <span className="sr-only">Delete</span>
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Delete account</p>
              </TooltipContent>
            </Tooltip>
          )}
        </TooltipProvider>
      </div>
      
      <FollowLinksUploadDialog
        isOpen={isFollowLinksDialogOpen}
        onClose={() => setIsFollowLinksDialogOpen(false)}
        account={account}
      />
    </>
  );
};

export default AccountActions;
